import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import Win10AdminLayout from "../layouts/Win10AdminLayout";

function asText(value) {
  return String(value || "").trim();
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

function flattenErrors(errors) {
  if (!errors || typeof errors !== "object") return "";
  const pairs = Object.entries(errors)
    .slice(0, 5)
    .map(([field, messages]) => {
      const list = Array.isArray(messages) ? messages : [messages];
      return `${field}: ${list.slice(0, 2).join(", ")}`;
    });
  return pairs.join(" | ");
}

function truncate(value, max = 120) {
  const text = asText(value);
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function getStatusCode(row) {
  return Number(row?.wijzigingen?.statusCode) || 0;
}

function getSeverity(row) {
  const explicit = asText(row?.severity).toLowerCase();
  if (explicit === "error" || explicit === "warning") return explicit;
  return getStatusCode(row) >= 500 ? "error" : "warning";
}

function getFlowStatus(row) {
  const explicit = asText(row?.status).toLowerCase();
  if (explicit === "new" || explicit === "seen" || explicit === "resolved") return explicit;
  return "new";
}

function isLikelyDuplicate(current, previous) {
  if (!current || !previous) return false;
  const currentCode = asText(current?.wijzigingen?.code);
  const previousCode = asText(previous?.wijzigingen?.code);
  const currentPath = asText(current?.wijzigingen?.path);
  const previousPath = asText(previous?.wijzigingen?.path);
  if (!currentCode || !currentPath) return false;
  if (currentCode !== previousCode || currentPath !== previousPath) return false;

  const currentTs = new Date(current?.createdAt).getTime();
  const previousTs = new Date(previous?.createdAt).getTime();
  if (!Number.isFinite(currentTs) || !Number.isFinite(previousTs)) return false;
  return Math.abs(currentTs - previousTs) <= 5 * 60 * 1000;
}

function SeverityBadge({ value }) {
  const isError = value === "error";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isError ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
      }`}
    >
      {isError ? "error" : "warning"}
    </span>
  );
}

function FlowStatusBadge({ value }) {
  const styles = {
    new: "bg-blue-100 text-blue-700",
    seen: "bg-slate-100 text-slate-700",
    resolved: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[value] || styles.new}`}>
      {value}
    </span>
  );
}

export default function BeheerApiFouten() {
  const [searchParams] = useSearchParams();
  const initialStatusFilter = (() => {
    const value = asText(searchParams.get("status")).toLowerCase();
    if (value === "new" || value === "seen" || value === "resolved") return value;
    return "alle";
  })();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [httpFilter, setHttpFilter] = useState("alle");
  const [severityFilter, setSeverityFilter] = useState("alle");
  const [flowStatusFilter, setFlowStatusFilter] = useState(initialStatusFilter);
  const [tenantFilter, setTenantFilter] = useState("alle");
  const [codeFilter, setCodeFilter] = useState("alle");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/auditlogs", {
          params: { action: "api_error", limit: 500 },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setRows(list);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Kon API-fouten niet ophalen");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function updateStatus(item, nextStatus) {
    if (!item?._id) return;
    setUpdatingId(item._id);
    setError("");
    try {
      const res = await api.patch(`/auditlogs/${item._id}/status`, { status: nextStatus });
      const updated = res?.data?.item;
      setRows((prev) =>
        prev.map((row) => {
          if (row._id !== item._id) return row;
          if (updated && updated._id === item._id) return updated;
          return { ...row, status: nextStatus };
        })
      );
      setSelected((prev) => {
        if (!prev || prev._id !== item._id) return prev;
        if (updated && updated._id === item._id) return updated;
        return { ...prev, status: nextStatus };
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Status bijwerken mislukt");
    } finally {
      setUpdatingId("");
    }
  }

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aTime = new Date(a?.createdAt).getTime() || 0;
      const bTime = new Date(b?.createdAt).getTime() || 0;
      return bTime - aTime;
    });
  }, [rows]);

  const codeOptions = useMemo(() => {
    return Array.from(new Set(sortedRows.map((row) => asText(row?.wijzigingen?.code)).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, "nl-NL")
    );
  }, [sortedRows]);

  const tenantOptions = useMemo(() => {
    const seen = new Map();
    for (const row of sortedRows) {
      const tenantId = asText(row?.tenantId);
      if (!tenantId || seen.has(tenantId)) continue;
      seen.set(tenantId, asText(row?.tenantName) || tenantId);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1], "nl-NL"));
  }, [sortedRows]);

  const filtered = useMemo(() => {
    return sortedRows.filter((row) => {
      const statusCode = getStatusCode(row);
      const severity = getSeverity(row);
      const flowStatus = getFlowStatus(row);
      const code = asText(row?.wijzigingen?.code);
      const path = asText(row?.wijzigingen?.path);
      const message = asText(row?.wijzigingen?.message);
      const method = asText(row?.wijzigingen?.method);
      const userNaam = asText(row?.userNaam);
      const tenantId = asText(row?.tenantId);
      const tenantName = asText(row?.tenantName);
      const errorSummary = flattenErrors(row?.wijzigingen?.errors);

      if (httpFilter !== "alle") {
        if (httpFilter === "4xx" && (statusCode < 400 || statusCode >= 500)) return false;
        if (httpFilter === "5xx" && statusCode < 500) return false;
      }

      if (severityFilter !== "alle" && severity !== severityFilter) return false;
      if (flowStatusFilter !== "alle" && flowStatus !== flowStatusFilter) return false;
      if (tenantFilter !== "alle" && tenantId !== tenantFilter) return false;
      if (codeFilter !== "alle" && code !== codeFilter) return false;

      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [code, path, message, method, userNaam, tenantId, tenantName, errorSummary].some((value) =>
        value.toLowerCase().includes(q)
      );
    });
  }, [sortedRows, httpFilter, severityFilter, flowStatusFilter, tenantFilter, codeFilter, query]);

  if (loading) {
    return (
      <Win10AdminLayout>
        <div className="p-6 text-gray-600">API-fouten laden...</div>
      </Win10AdminLayout>
    );
  }
  if (error && rows.length === 0) {
    return (
      <Win10AdminLayout>
        <div className="p-6 text-red-600">{error}</div>
      </Win10AdminLayout>
    );
  }

  const selectedErrorText = flattenErrors(selected?.wijzigingen?.errors);

  return (
    <Win10AdminLayout>
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-3">API fouten</h1>
      <p className="text-gray-600 mb-5">
        Overzicht van backend API-fouten per tenant. Klik op een regel voor details en markeer meldingen als gezien of opgelost.
      </p>

      {error ? <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

      <div className="mb-4 flex flex-wrap gap-3">
        <select value={httpFilter} onChange={(event) => setHttpFilter(event.target.value)} className="border rounded-md px-3 py-2">
          <option value="alle">Alle HTTP statussen</option>
          <option value="4xx">Alleen 4xx</option>
          <option value="5xx">Alleen 5xx</option>
        </select>

        <select
          value={severityFilter}
          onChange={(event) => setSeverityFilter(event.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="alle">Alle severities</option>
          <option value="error">error</option>
          <option value="warning">warning</option>
        </select>

        <select
          value={flowStatusFilter}
          onChange={(event) => setFlowStatusFilter(event.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="alle">Alle statussen</option>
          <option value="new">new</option>
          <option value="seen">seen</option>
          <option value="resolved">resolved</option>
        </select>

        <select value={codeFilter} onChange={(event) => setCodeFilter(event.target.value)} className="border rounded-md px-3 py-2">
          <option value="alle">Alle foutcodes</option>
          {codeOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>

        <select
          value={tenantFilter}
          onChange={(event) => setTenantFilter(event.target.value)}
          className="border rounded-md px-3 py-2"
        >
          <option value="alle">Alle tenants</option>
          {tenantOptions.map(([tenantId, tenantName]) => (
            <option key={tenantId} value={tenantId}>
              {tenantName}
            </option>
          ))}
        </select>

        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Zoek op route, code, message, tenant, gebruiker..."
          className="border rounded-md px-3 py-2 min-w-[320px]"
        />
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">Tijd</th>
              <th className="px-3 py-2 text-left">Severity</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Route</th>
              <th className="px-3 py-2 text-left">Message</th>
              <th className="px-3 py-2 text-left">Tenant</th>
              <th className="px-3 py-2 text-left">Acties</th>
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-5 text-center text-gray-500">
                  Geen API-fouten gevonden voor deze filters.
                </td>
              </tr>
            ) : (
              filtered.map((row, index) => {
                const statusCode = getStatusCode(row);
                const severity = getSeverity(row);
                const flowStatus = getFlowStatus(row);
                const duplicate = isLikelyDuplicate(row, filtered[index - 1]);

                return (
                  <tr
                    key={row._id}
                    className={`border-t hover:bg-gray-50 ${duplicate ? "bg-amber-50/40" : ""}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setSelected(row)}>
                      <div>{formatDateTime(row.createdAt)}</div>
                      <div className="text-xs text-gray-500">{statusCode || "-"}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setSelected(row)}>
                      <SeverityBadge value={severity} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setSelected(row)}>
                      <FlowStatusBadge value={flowStatus} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap cursor-pointer" onClick={() => setSelected(row)}>
                      {asText(row?.wijzigingen?.code) || "-"}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[260px] truncate cursor-pointer"
                      title={`${asText(row?.wijzigingen?.method)} ${asText(row?.wijzigingen?.path)}`}
                      onClick={() => setSelected(row)}
                    >
                      {truncate(`${asText(row?.wijzigingen?.method)} ${asText(row?.wijzigingen?.path)}`, 80) || "-"}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[320px] truncate cursor-pointer"
                      title={asText(row?.wijzigingen?.message)}
                      onClick={() => setSelected(row)}
                    >
                      {truncate(row?.wijzigingen?.message, 90) || "-"}
                    </td>
                    <td className="px-3 py-2 max-w-[220px] truncate cursor-pointer" onClick={() => setSelected(row)}>
                      {truncate(asText(row?.tenantName) || asText(row?.tenantId) || "-", 50)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => updateStatus(row, "seen")}
                          disabled={updatingId === row._id || flowStatus === "seen"}
                          className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
                        >
                          Gezien
                        </button>
                        <button
                          type="button"
                          onClick={() => updateStatus(row, "resolved")}
                          disabled={updatingId === row._id || flowStatus === "resolved"}
                          className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 disabled:opacity-50"
                        >
                          Opgelost
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selected ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-lg font-semibold">API fout details</h2>
              <button type="button" onClick={() => setSelected(null)} className="rounded border px-2 py-1 text-sm">
                Sluiten
              </button>
            </div>

            <div className="grid gap-3 px-4 py-4 text-sm md:grid-cols-2">
              <div>
                <div className="text-gray-500">Tijd</div>
                <div>{formatDateTime(selected.createdAt)}</div>
              </div>
              <div>
                <div className="text-gray-500">Foutcode</div>
                <div>{asText(selected?.wijzigingen?.code) || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">Severity</div>
                <div>
                  <SeverityBadge value={getSeverity(selected)} />
                </div>
              </div>
              <div>
                <div className="text-gray-500">Status</div>
                <div>
                  <FlowStatusBadge value={getFlowStatus(selected)} />
                </div>
              </div>
              <div>
                <div className="text-gray-500">Methode + route</div>
                <div>{`${asText(selected?.wijzigingen?.method)} ${asText(selected?.wijzigingen?.path)}`.trim() || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">Gebruiker</div>
                <div>{asText(selected?.userNaam) || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">Tenant</div>
                <div>{asText(selected?.tenantName) || asText(selected?.tenantId) || "-"}</div>
              </div>
              <div>
                <div className="text-gray-500">HTTP status</div>
                <div>{getStatusCode(selected) || "-"}</div>
              </div>
            </div>

            <div className="border-t px-4 py-4 text-sm">
              <div className="mb-1 text-gray-500">Volledige message</div>
              <div className="mb-4 whitespace-pre-wrap rounded border bg-gray-50 px-3 py-2">
                {asText(selected?.wijzigingen?.message) || "-"}
              </div>

              <div className="mb-1 text-gray-500">Validatiefouten</div>
              <div className="whitespace-pre-wrap rounded border bg-gray-50 px-3 py-2">
                {selectedErrorText || "-"}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </Win10AdminLayout>
  );
}
